import './locations.scss';
import { StoreFinderMap } from './../store-finder/store-finder-map';
import { smoothScrollTo, getOffset, createElementFromHTML } from '../../utilities/js/helper';
import { Loader } from '@googlemaps/js-api-loader';
import Swiper from 'swiper';
import { Mousewheel, Pagination } from 'swiper/modules';

class StoreFinder {
    constructor ($container) {
        this.$storeFinder = $container;

        this.$form = this.$storeFinder.querySelector('form');
        this.$locationInput = this.$storeFinder.querySelector('[data-store-finder="location"]');
        this.$inputDeleteButton = this.$storeFinder.querySelector('[data-store-finder="delete-input"]');
        this.lastLocationValue = '';
        this.autoScroll = false;
        this.$submitButton = this.$storeFinder.querySelector('[data-form-submit]');
        this.inputLAT = this.$storeFinder.querySelector('[data-location="lat"]');
        this.inputLNG = this.$storeFinder.querySelector('[data-location="lng"]');
        this.inputFilter = this.$storeFinder.querySelector('[data-location="filter"]');
        this.countryRestriction = { country: ['DE'] };
        this.foundEvent = new CustomEvent('changeInput', {
            view: window,
            bubbles: true,
            cancelable: false
        });

        this.autocomplete = null;
        this.$storeResult = document.querySelector('[data-store-finder="results"]');
        this.$storeResultItems = this.$storeResult.querySelectorAll('[data-store-finder="store"]');
        this.$radius = document.querySelector('[data-select][id="radius"]');

        this.$storeResultText = this.$storeFinder.querySelector('[data-store-finder="resulttext"]');
        this.$storeResultTextWrapper = this.$storeFinder.querySelector('[data-store-resulttext-wrapper]');
        this.$storeFinderMap = this.$storeFinder.querySelector('[data-store-finder="map"]');
        if (this.$storeFinderMap) {
            this.$storeFinderStoreUrl = this.$storeFinderMap.getAttribute('data-store-finder-store-url');
        }
        this.$storeFinderMapContainer = this.$storeFinder.querySelector('[data-store-finder="mapcontainer"]');

        this.$locationContainer = this.$storeFinder.querySelector('[data-store-finder="location-container"]');
        this.$locationScrollPosition = this.$storeFinder.querySelector('[data-store-finder="location-scroll-pos"]');
        this.$storeFinderMobileListSwitch = this.$storeFinder.querySelector('input[id="storefinder-list"]');

        this.storeMap = null;

        this.foundLocation = false;
        window.storeSlider = null;

        this.activeRequest = false;

        this.googleAPIKey = googleMapsApiKey; // eslint-disable-line
    }

    initialize () {
        if (this.$storeFinderMap && this.googleAPIKey !== '') {
            const loader = new Loader({
                apiKey: this.googleAPIKey,
                version: 'quarterly',
                libraries: ['places']
            });

            loader.load().then(() => {
                if (typeof google !== 'undefined') {
                    this.storeMap = new StoreFinderMap(this.$storeFinderMap, {
                        centerLat: this.$storeFinderMap.getAttribute('data-lat'),
                        centerLng: this.$storeFinderMap.getAttribute('data-lng')
                    });

                    this.setEvents();
                    this.storeMap.setVisualRadius(this.inputLAT.value, this.inputLNG.value, this.$radius.value);
                } else {
                    const $waitForGoogle = setInterval(() => {
                        if (typeof google !== 'undefined') {
                            clearInterval($waitForGoogle);
                            this.storeMap = new StoreFinderMap(this.$storeFinderMap, this, {
                                centerLat: this.$storeFinderMap.getAttribute('data-lat'),
                                centerLng: this.$storeFinderMap.getAttribute('data-lng')
                            });

                            this.setEvents();
                            this.storeMap.setVisualRadius(this.inputLAT.value, this.inputLNG.value, this.$radius.value);
                        }
                    }, 100);
                }
            });
        }

        if (this.$storeResultItems) {
            if (this.$storeResultItems.length > 0) {
                this.initSlider();
            }
        }
    }

    scrollTo ($target) {
        smoothScrollTo(0, getOffset($target).top, 500);
    }

    initSlider () {
        this.$storeResult = document.querySelector('[data-store-finder="results"]');
        this.$storeResultItems = this.$storeResult.querySelectorAll('[data-store-finder="store"]');

        Swiper.use([Mousewheel]);
        Swiper.use([Pagination]);

        window.storeSlider = new Swiper(this.$storeResult, {
            slidesPerView: 'auto',
            direction: 'vertical',
            freeMode: {
                enabled: true,
                sticky: true
            },
            mousewheel: {
                forceToAxis: true
            },
            pagination: {
                type: 'progressbar',
                el: document.querySelector('[data-store-finder-result-pagination]')
            },
            observer: true,
            watchSlidesProgress: true,
            spaceBetween: 40,
            speed: 500
        });

        this.setSliderEvents();
    }

    setSliderEvents () {
        this.$storeResultItems.forEach((store) => {
            const id = parseInt(store.getAttribute('data-store-id'));

            store.addEventListener('click', () => {
                store.classList.add('is--active');
                this.storeMap.setActiveMarkerWithId(id);
            });
        });
    }

    destroySlider (slider, callback) {
        if (slider !== null) {
            slider.destroy(true, true);
            slider = null;
        }

        callback();
    }

    dispatchForCode (event, callback) {
        let code;

        if (event.key !== undefined) {
            code = event.key;
        } else if (event.keyIdentifier !== undefined) {
            code = event.keyIdentifier;
        } else if (event.keyCode !== undefined) {
            code = event.keyCode;
        }

        callback(code);
    }

    // Update usercentrics icon position
    updateUsercentrics () {
        const $usercentrics = document.getElementById('usercentrics-root');

        if ($usercentrics) {
            const $shadowRoot = $usercentrics.shadowRoot;
            const $styleTag = $shadowRoot.querySelector('[custom-style]');
            let space;

            if (window.innerWidth < 768 && this.$locationScrollPosition.classList.contains('is--fixed')) {
                space = 175;
            } else {
                space = 0;
            }

            const styleInformation = `\n button[data-testid] { margin-bottom: ${space}px; transition: margin-bottom 0.3s cubic-bezier(0.25, 1, 0.5, 1)`;

            if (!$styleTag) {
                const $styleTag = document.createElement('style');
                $styleTag.setAttribute('custom-style', '');
                $styleTag.innerHTML = styleInformation;
                $shadowRoot.appendChild($styleTag);
            } else {
                $styleTag.innerHTML = styleInformation;
            }
        }
    }

    setEvents () {
        this.$radius.addEventListener('change', () => {
            this.$submitButton.classList.remove('is--disabled');
            this.ajaxRequest(this.$form.getAttribute('action'), false);
        });

        this.$storeFinderMobileListSwitch.addEventListener('change', () => {
            if (this.$storeFinderMobileListSwitch.checked) {
                window.storeSlider.update();
            }
        });
    }

    setResult (data, scroll) {
        const markers = JSON.parse(data.marker);

        if (data.resultText) {
            this.setStoreResultText(data.resultText);
        }

        const storeResults = createElementFromHTML(data.result);

        this.destroySlider(window.storeSlider, () => {
            const $swiperWrapper = this.$storeResult.querySelector('.swiper-wrapper');

            if ($swiperWrapper) {
                $swiperWrapper.remove();
            }

            if (storeResults.length > 1) {
                storeResults.forEach((item) => {
                    this.$storeResult.appendChild(item);
                });
            } else {
                this.$storeResult.appendChild(storeResults);
            }
        });

        if (scroll) {
            this.scrollTo(this.$storeResult);
        }

        this.initSlider();

        this.storeMap.renewMarker(markers);
        this.storeMap.setVisualRadius(this.inputLAT.value, this.inputLNG.value, this.$radius.value);
    }

    setStoreResultText (resultText) {
        this.$storeResultTextWrapper.classList.remove('is--hidden');
        this.$storeResultText.innerHTML = resultText;
    }
}

export { StoreFinder };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $storeFinder = $context.querySelector('[data-store-finder="root"]');
        if ($storeFinder) {
            const stores = new StoreFinder($storeFinder);
            stores.initialize();
        }
    }
});
